import {useState, useEffect} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {User} from '../../../model/user/User';
import {AuthService} from '../../../service/AuthService';
import {ApiError} from "../../../service/HttpService";

interface EmailVerificationBannerProps {
    user: User;
}

export default function EmailVerificationBanner({user}: EmailVerificationBannerProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [lastSentTime, setLastSentTime] = useState<number | null>(null);
    const [secondsRemaining, setSecondsRemaining] = useState(0);

    // Update countdown timer
    useEffect(() => {
        if (lastSentTime) {
            const interval = setInterval(() => {
                const elapsed = Date.now() - lastSentTime;
                const remaining = Math.max(0, 60 - Math.floor(elapsed / 1000));
                setSecondsRemaining(remaining);

                if (remaining === 0) {
                    clearInterval(interval);
                }
            }, 1000);

            // Calculate initial seconds remaining
            const elapsed = Date.now() - lastSentTime;
            const remaining = Math.max(0, 60 - Math.floor(elapsed / 1000));
            setSecondsRemaining(remaining);

            return () => clearInterval(interval);
        }
    }, [lastSentTime]);

    // Don't show banner if:
    // - User is not from new flow
    // - Email is already verified
    // - User hasn't completed the initial quiz yet
    if (user.emailVerified || !user.hasSolvedInitialQuiz) {
        return null;
    }

    const canResend = !lastSentTime || secondsRemaining === 0;

    const handleSendVerificationCode = async () => {
        if (!canResend || isSending) return;

        setIsSending(true);
        setMessage(null);

        try {
            await AuthService.sendEmailVerificationCode(user.id);
            setMessage({
                type: 'success',
                text: `Kod weryfikacyjny został wysłany na ${user.email}`
            });
            setLastSentTime(Date.now());
            setIsExpanded(true);
        } catch (error) {
            const apiError = error as ApiError
            setMessage({
                type: 'error',
                text: apiError.message || 'Wystąpił błąd podczas wysyłania kodu. Spróbuj ponownie.'
            });
        } finally {
            setIsSending(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{height: 0, opacity: 0}}
                animate={{height: 'auto', opacity: 1}}
                exit={{height: 0, opacity: 0}}
                transition={{duration: 0.3}}
                style={{
                    background: 'linear-gradient(90deg, #2a2215 0%, #1e1b14 100%)',
                    borderBottom: '1px solid rgba(245, 158, 12, 0.25)',
                }}
            >
                <div style={{maxWidth: '1160px', margin: '0 auto', padding: '0.55rem 1rem'}}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap" style={{gap: '0.5rem'}}>
                        <div className="d-flex align-items-center flex-wrap" style={{gap: '0.3rem 0.6rem'}}>
                            <i className="bi bi-envelope-exclamation" style={{color: '#f59e0c', fontSize: '0.95rem'}}/>
                            <span style={{color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem', fontWeight: 600}}>
                                Potwierdź swój email
                            </span>
                            <span style={{color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem'}}>
                                aby odblokować wszystkie funkcje
                            </span>
                        </div>

                        <div className="d-flex align-items-center" style={{gap: '0.4rem'}}>
                            <button
                                onClick={() => setIsExpanded(!isExpanded)}
                                style={{
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: '8px',
                                    color: 'rgba(255,255,255,0.5)',
                                    fontSize: '0.72rem',
                                    padding: '0.3rem 0.65rem',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    transition: 'all 0.15s',
                                }}
                            >
                                <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} me-1`}
                                   style={{fontSize: '0.6rem'}}/>
                                Info
                            </button>

                            <button
                                onClick={handleSendVerificationCode}
                                disabled={!canResend || isSending}
                                style={{
                                    background: canResend && !isSending ? '#f59e0c' : 'rgba(245, 158, 12, 0.3)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: canResend && !isSending ? '#1a1a2e' : 'rgba(255,255,255,0.4)',
                                    fontSize: '0.72rem',
                                    padding: '0.3rem 0.75rem',
                                    cursor: canResend && !isSending ? 'pointer' : 'default',
                                    fontWeight: 700,
                                    transition: 'all 0.15s',
                                }}
                            >
                                {isSending ? (
                                    <span className="spinner-border spinner-border-sm"
                                          style={{width: '0.65rem', height: '0.65rem', borderWidth: '0.12em'}}/>
                                ) : (
                                    <>
                                        <i className="bi bi-send me-1" style={{fontSize: '0.65rem'}}/>
                                        {lastSentTime ? 'Wyślij ponownie' : 'Wyślij kod'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                initial={{height: 0, opacity: 0}}
                                animate={{height: 'auto', opacity: 1}}
                                exit={{height: 0, opacity: 0}}
                                transition={{duration: 0.2}}
                            >
                                <div style={{
                                    marginTop: '0.6rem',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '10px',
                                    background: 'rgba(0,0,0,0.25)',
                                    border: '1px solid rgba(245, 158, 12, 0.1)',
                                }}>
                                    <div className="d-flex flex-wrap" style={{gap: '1.5rem'}}>
                                        <div style={{flex: '1 1 280px', minWidth: 0}}>
                                            <div style={{color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.4rem'}}>
                                                Jak to działa?
                                            </div>
                                            <div className="d-flex flex-column" style={{gap: '0.25rem'}}>
                                                {['Kliknij "Wyślij kod"', 'Sprawdź swoją skrzynkę email', 'Kliknij link w emailu', 'Ustaw hasło do konta'].map(function (step, i) {
                                                    return (
                                                        <div key={i} className="d-flex align-items-center" style={{gap: '0.4rem'}}>
                                                            <span style={{
                                                                width: '16px', height: '16px',
                                                                borderRadius: '50%',
                                                                background: 'rgba(245, 158, 12, 0.15)',
                                                                color: '#f59e0c',
                                                                fontSize: '0.6rem',
                                                                fontWeight: 700,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                flexShrink: 0,
                                                            }}>{i + 1}</span>
                                                            <span style={{color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem'}}>{step}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div style={{flex: '1 1 200px', minWidth: 0}}>
                                            <div style={{color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.4rem'}}>
                                                Dlaczego warto?
                                            </div>
                                            <div className="d-flex flex-column" style={{gap: '0.2rem'}}>
                                                {['Ochrona konta', 'Ustawienie hasła', 'Pełny dostęp do platformy'].map(function (item, i) {
                                                    return (
                                                        <div key={i} className="d-flex align-items-center" style={{gap: '0.35rem'}}>
                                                            <i className="bi bi-check-circle-fill" style={{color: '#2ad167', fontSize: '0.65rem', flexShrink: 0}}/>
                                                            <span style={{color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem'}}>{item}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    {message && (
                                        <div style={{
                                            marginTop: '0.6rem',
                                            padding: '0.4rem 0.7rem',
                                            borderRadius: '8px',
                                            background: message.type === 'success' ? 'rgba(42, 209, 103, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                                            border: `1px solid ${message.type === 'success' ? 'rgba(42, 209, 103, 0.2)' : 'rgba(220, 53, 69, 0.2)'}`,
                                            fontSize: '0.72rem',
                                            color: message.type === 'success' ? '#2ad167' : '#dc3545',
                                        }}>
                                            <i className={`bi bi-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-1`}/>
                                            {message.text}
                                        </div>
                                    )}

                                    {!canResend && secondsRemaining > 0 && (
                                        <div style={{marginTop: '0.4rem', color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem'}}>
                                            <i className="bi bi-clock me-1"/>
                                            Możesz wysłać ponownie za {secondsRemaining}s
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
